// src/packages/chubb/travel/chubb_travel_student_2019/TravelStudentDetails.jsx
import React, { useMemo } from "react";
import "./travel_student.css";

/**
 * Chubb — Travel Easy Overseas Student (S1–S3)
 * Zero-index safe: ไม่มีตัวแปรชื่อ `index` ใน JSX และไม่มี `{ ... }` ในข้อความธรรมดา
 */

const fmt = (x) =>
  x === null || x === undefined ? "—" : typeof x === "number" ? x.toLocaleString() : x;

const PLANS = ["Student 1", "Student 2", "Student 3"];

// ผลประโยชน์หลัก (ปรับได้ตามโบรชัวร์)
const benefits = [
  { key: "med",   name: "ค่ารักษาพยาบาล (บาท)",                       vals: [5_500_000, 2_500_000, 2_000_000] },
  { key: "evac",  name: "เคลื่อนย้ายฉุกเฉิน/ส่งกลับประเทศ (บาท)",    vals: [3_000_000, 2_000_000, 1_500_000] },
  { key: "repat", name: "ส่งศพกลับประเทศไทย (บาท)",                    vals: [1_000_000, 1_000_000, 1_000_000] },
  { key: "liab",  name: "ความรับผิดต่อบุคคลภายนอก (บาท)",            vals: [100_000, 100_000, 100_000] },
  { key: "bagg",  name: "กระเป๋า/ทรัพย์สินกับผู้ขนส่งสาธารณะ (บาท)", vals: [350_000, 300_000, 200_000] },
  { key: "bagg_item", name: "จำกัดต่อชิ้น/คู่/ชุด (บาท)",              vals: [10_000, 10_000, 10_000] },
  { key: "delay6h",   name: "เที่ยวบินล่าช้า — จ่ายทุก ๆ 6 ชั่วโมง (บาท)", vals: [2_000, 2_000, 2_000] },
  { key: "miss_conn", name: "พลาดการต่อเครื่อง — จ่ายทุก ๆ 6 ชม. (บาท)",   vals: [2_000, 2_000, null] },
  { key: "study_int", name: "ชดเชยค่าเล่าเรียน (บาท)",                 vals: [5_500_000, 4_000_000, 3_000_000] },
  { key: "adl_parent",name: "อุบัติเหตุเสียชีวิตของบิดามารดา (บาท)",  vals: [100_000, 60_000, 40_000] },
];

// ตารางเบี้ยจากโบรชัวร์ (บาท) — ระยะเวลา 3–24 เดือน
const premiums = {
  rows: [
    { months: 3,  s1: 12200, s2:  6000, s3:  4700 },
    { months: 4,  s1: 14800, s2:  7300, s3:  5700 },
    { months: 5,  s1: 18000, s2:  8900, s3:  6900 },
    { months: 6,  s1: 19950, s2:  9950, s3:  7450 },
    { months: 7,  s1: 23350, s2: 11550, s3:  8690 },
    { months: 8,  s1: 26750, s2: 13050, s3:  9950 },
    { months: 9,  s1: 30150, s2: 14650, s3: 11250 },
    { months: 10, s1: 33450, s2: 16250, s3: 12550 },
    { months: 11, s1: 36850, s2: 18150, s3: 13750 },
    { months: 12, s1: 39900, s2: 19900, s3: 14900 },
    { months: 13, s1: 43300, s2: 21600, s3: 16100 },
    { months: 14, s1: 46700, s2: 23300, s3: 17400 },
    { months: 15, s1: 50100, s2: 25000, s3: 18700 },
    { months: 16, s1: 53500, s2: 26700, s3: 19900 },
    { months: 17, s1: 56700, s2: 28300, s3: 21100 },
    { months: 18, s1: 59850, s2: 29800, s3: 22350 },
    { months: 19, s1: 63300, s2: 31600, s3: 23700 },
    { months: 20, s1: 66700, s2: 33300, s3: 24900 },
    { months: 21, s1: 70100, s2: 35000, s3: 26100 },
    { months: 22, s1: 73500, s2: 36700, s3: 27400 },
    { months: 23, s1: 76700, s2: 38300, s3: 28600 },
    { months: 24, s1: 79800, s2: 39800, s3: 29800 },
  ],
};

export default function TravelStudentDetails({ onQuote }) {
  const hasPremium = useMemo(() => premiums.rows.some(r => r.s1 || r.s2 || r.s3), []);
  return (
    <div className="pkg-detail chubb-travel-student">
      <h2>Chubb Travel Easy — Overseas Student (S1–S3)</h2>
      <p className="pkg-note">
        สำหรับนักเรียน/นักศึกษาต่างประเทศ ครอบคลุมค่ารักษาพยาบาล การเคลื่อนย้ายฉุกเฉิน
        ความรับผิดต่อบุคคลภายนอก กระเป๋าเดินทาง และความล่าช้า เลือกระยะเวลา 3–24 เดือน
      </p>

      {/* Benefits */}
      <h3 className="pkg-section">ผลประโยชน์ต่อแผน</h3>
      <table className="pkg-table">
        <thead>
          <tr>
            <th className="pkg-left">ความคุ้มครอง</th>
            {PLANS.map((p) => <th key={`h-${p}`}>{p}</th>)}
          </tr>
        </thead>
        <tbody>
          {benefits.map((b) => (
            <tr key={`bf-${b.key}`}>
              <td className="pkg-left">{b.name}</td>
              {(b.vals || []).map((v, i) => <td key={`bf-${b.key}-${i}`}>{fmt(v)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Premium table */}
      <h3 className="pkg-section">เบี้ยประกันภัย (บาท) — ระยะเวลา 3–24 เดือน</h3>
      <table className="pkg-table">
        <thead>
          <tr>
            <th>ระยะเวลา (เดือน)</th>
            {PLANS.map((p) => <th key={`ph-${p}`}>{p}</th>)}
          </tr>
        </thead>
        <tbody>
          {premiums.rows.map((r) => (
            <tr key={`pr-${r.months}`}>
              <td>{r.months}</td>
              <td>{fmt(r.s1)}</td>
              <td>{fmt(r.s2)}</td>
              <td>{fmt(r.s3)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="pkg-foot">
        <button className="pkg-quote-btn" onClick={onQuote} aria-label="ขอใบเสนอราคา Chubb Travel Student">
          ขอใบเสนอราคา
        </button>
      </div>
    </div>
  );
}
